/**
 * InlineVideoPlayer
 *
 * Generic, theme-aware encrypted-HLS video surface with the SAME look and
 * controls as the user-generated content player, minus the social chrome. It is
 * the lightweight sibling of `ContentComponent` (which is `VideoComponent` +
 * likes/comments/description/links): this is `VideoComponent` + the shared
 * `VideoProgressBar` + `VideoFullscreenButton`, wrapped in a themed,
 * layout-constrained container.
 *
 * Reused anywhere a single encrypted video must play inline without content UI —
 * the encrypted chat-media `MediaDetailsPage` today, Community group-room video
 * next, and other one-off surfaces. It is intentionally NOT named for any one
 * feature.
 *
 * The caller resolves the CDN `videoUrl` + decrypted `VideoDecryptionKeys`
 * through its OWN pipeline (e.g. `InitializeChatVideoUseCase`) and passes them
 * in; this component injects them into the shared `videoSlice`
 * (`setInitializedVideo`) so `VideoComponent`, `VideoProgressBar`, the
 * `videoElementRegistry`, and native fullscreen all work exactly as they do for
 * user-gen content — giving consistent playback across the app. A synthetic
 * `UserMediaEntity` (the only thing `VideoComponent` needs) is built from the
 * primitives passed here, so callers never have to fabricate a full entity.
 *
 * Encrypted thumbnails: chat / Community media thumbnails are E2EE, not plain
 * CDN URLs, so the caller passes a ready-to-render `thumbnail` node (typically
 * `EncryptedCachedImage`) shown until playback starts.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import type { AppDispatch } from '@/presentation/store';
import { setInitializedVideo } from '@/presentation/store/slices/video/videoSlice';
import { VideoComponent } from '@/presentation/components/video/VideoComponent';
import { VideoProgressBar } from '@/presentation/components/video/VideoProgressBar';
import { VideoFullscreenButton } from '@/presentation/components/video/VideoFullscreenButton';
import { videoElementRegistry } from '@/presentation/registries/videoElementRegistry';
import { useVideoManagement } from '@/presentation/feature/content/hooks/useVideoManagement';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { VideoDecryptionKeys } from '@/domain/entities/video/videoDecryptionEntity';
import { MediaType } from '@/domain/enums/mediaType';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { VIDEO_CONSTANTS } from '@/domain/constants/videoConstants';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';

export interface InlineVideoPlayerProps {
  /** Screen key for video management / identification (mint once per page). */
  screenKey: string;

  /** Stable unique id for this video — `media.id` + the `videoKey` component. */
  videoId: string;

  /** Resolved CDN master-playlist URL (with `?hdnts=` token). */
  videoUrl: string;

  /** Decrypted AES-128 keys for the encrypted hls.js loader. */
  decryptionKeys: VideoDecryptionKeys;

  /**
   * Custom thumbnail node shown until playback starts (e.g. `EncryptedCachedImage`
   * for E2EE media). Omit for a thumbnail-less surface.
   */
  thumbnail?: React.ReactNode;

  /** Media aspect ratio (width/height) for sizing. Defaults to portrait 9:16. */
  aspectRatio?: number;

  /** Known media duration in milliseconds, if any. The element's own metadata
   *  duration takes over once loaded, so this is only the initial estimate. */
  durationMs?: number;

  /** Whether to start playback automatically once ready. Defaults to true. */
  autoplay?: boolean;

  /** Whether to start muted (required for autoplay under browser policy). Default true. */
  muted?: boolean;

  /** Surface a fatal playback error to the caller. */
  onError?: (error: Error) => void;
}

export const InlineVideoPlayer: React.FC<InlineVideoPlayerProps> = ({
  screenKey,
  videoId,
  videoUrl,
  decryptionKeys,
  thumbnail,
  aspectRatio = VIDEO_CONSTANTS.DEFAULT_ASPECT_RATIO,
  durationMs,
  autoplay = true,
  muted = true,
  onError,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const dispatch = useDispatch<AppDispatch>();
  const videoManagement = useVideoManagement();

  const videoKey = useMemo(
    () => MediaUtils.composeVideoPlayerKey(screenKey, videoId),
    [screenKey, videoId],
  );

  // `VideoProgressBar` needs a positive duration. Seed from the caller's estimate
  // (if any), then let the element's real `loadedmetadata` duration take over.
  const [durationSec, setDurationSec] = useState<number>(durationMs ? durationMs / 1000 : 0);
  const [isVideoReady, setIsVideoReady] = useState(false);

  // Synthetic `UserMediaEntity` — the only thing `VideoComponent` needs. Content
  // fields are neutral defaults (this surface never shows social chrome).
  const media = useMemo<UserMediaEntity>(
    () => ({
      id: videoId,
      createdTimestamp: 0,
      updatedTimestamp: 0,
      isProcessing: false,
      type: MediaType.VIDEO,
      relativePath: '',
      aspectRatio,
      duration: durationMs,
      isDraft: false,
      allowComments: false,
      showCoins: false,
      shareLink: '',
      reports: 0,
      blocked: false,
      isStartUp: false,
      isLocal: false,
      isContent: false,
      allowUnlock: false,
      allowEarlyAccess: false,
    }),
    [videoId, aspectRatio, durationMs],
  );

  // Inject the already-resolved url + keys into the shared slice so the shared
  // VideoComponent/registry machinery treats this exactly like a user-gen video.
  useEffect(() => {
    dispatch(
      setInitializedVideo({
        videoKey,
        videoUrl,
        decryptionKeys,
        initializedAt: Date.now(),
      }),
    );
    // Re-run only when the identity/url changes (keys are 1:1 with the url).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, videoKey, videoUrl]);

  const handleVideoElementReady = useCallback(
    (_m: UserMediaEntity, element: HTMLVideoElement) => {
      const syncDuration = () => {
        if (Number.isFinite(element.duration) && element.duration > 0) {
          setDurationSec(element.duration);
        }
      };
      syncDuration();
      element.addEventListener('loadedmetadata', syncDuration);
      element.addEventListener('durationchange', syncDuration);
    },
    [],
  );

  const handlePlaybackStart = useCallback(() => {
    setIsVideoReady(true);
  }, []);

  // Bypass the user-gen `initVideo` autoplay path (we used `setInitializedVideo`),
  // so kick off muted playback ourselves once the manifest is ready.
  const handleReady = useCallback(() => {
    if (autoplay) {
      void videoManagement.playVideo(videoKey);
    }
  }, [autoplay, videoManagement, videoKey]);

  const handleError = useCallback(
    (_m: UserMediaEntity, error: Error) => {
      onError?.(error);
    },
    [onError],
  );

  const handleFullscreen = useCallback(() => {
    void videoManagement.enterVideoFullscreen(videoKey);
  }, [videoManagement, videoKey]);

  const containerStyle = useMemo<React.CSSProperties>(
    () => ({
      width: '100%',
      maxWidth: '100%',
      borderRadius: responsive.borderRadius.base,
      overflow: 'hidden',
      backgroundColor: theme.black,
      margin: '0 auto',
    }),
    [responsive.mediaPlayer.maxWidth, responsive.borderRadius.base, theme.black],
  );

  return (
    <div style={containerStyle}>
      {/* Relative stage: video + overlaid controls (mirrors ContentComponent's
          media block, without the social overlays). */}
      <div className="relative">
        <VideoComponent
          screenKey={screenKey}
          media={media}
          autoplay={autoplay}
          muted={muted}
          isFullscreen={false}
          thumbnail={thumbnail}
          onReady={handleReady}
          onPlaybackStart={handlePlaybackStart}
          onVideoElementReady={handleVideoElementReady}
          onFullscreenClick={handleFullscreen}
          onError={handleError}
        />

        {/* Bottom control bar: progress + fullscreen (post-init only). */}
        {isVideoReady && (
          <div
            className="absolute bottom-0 z-40 flex items-center"
            style={{
              left: responsive.spacing.lg,
              right: responsive.spacing.lg,
              gap: responsive.spacing.sm,
            }}
          >
            <div className="flex-1">
              <VideoProgressBar
                videoKey={videoKey}
                duration={durationSec}
                // Seek THIS video directly — `seekActiveVideo` targets the
                // active-key set by the user-gen `initializeVideo` flow we bypass.
                onSeek={(time) => videoElementRegistry.seek(videoKey, time)}
                isVideoReady={isVideoReady}
              />
            </div>
            <VideoFullscreenButton onClick={handleFullscreen} />
          </div>
        )}
      </div>
    </div>
  );
};
